import React from 'react';
import { themes, pageTypeMeta } from './themes';
import { Heart, Sun, Star, PenLine, Target, BookOpen } from 'lucide-react';

const iconMap = {
  'journal-daily': Sun,
  'journal-gratitude': Heart,
  'journal-prompt': PenLine,
  'journal-weekly': BookOpen,
  'journal-goals': Target,
  'journal-reflection': Star,
};

const JournalPage = ({ page, pageIndex, totalPages, theme }) => {
  const t = themes[theme] || themes['modern-minimal'];
  const meta = pageTypeMeta[page.pageType] || { label: 'Journal', color: t.accent };
  const Icon = iconMap[page.pageType] || PenLine;
  const prompts = page.prompts || [];

  const renderDailyEntry = () => (
    <div className="space-y-2 flex-1 flex flex-col justify-evenly">
      {(page.fields || []).map((field, fi) => (
        <div key={fi}>
          <span style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', fontWeight: 600, color: t.accent, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{field}</span>
          <div className="mt-0.5 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '20'}`, height: field.toLowerCase().includes('reflection') || field.toLowerCase().includes('refleksjon') ? '3rem' : '1.5rem' }} />
        </div>
      ))}
      {prompts.length > 0 && (
        <div className="mt-1 pt-1" style={{ borderTop: `1px dashed ${t.accent}30` }}>
          {prompts.map((p, pi) => (
            <div key={pi} className="mb-1">
              <p style={{ fontFamily: t.font, fontSize: '0.6rem', fontStyle: 'italic', color: t.accent, lineHeight: 1.4 }}>{p}</p>
              <div className="space-y-1.5 mt-1">
                {[0, 1].map(i => <div key={i} className="h-px w-full" style={{ backgroundColor: t.fieldBorder || t.accent + '15' }} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderGratitude = () => (
    <div className="space-y-2 flex-1 flex flex-col justify-evenly">
      {(page.fields || []).map((field, fi) => (
        <div key={fi} className="flex items-start gap-2">
          <Heart className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: t.accent }} strokeWidth={1.5} />
          <div className="flex-1">
            <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontWeight: 500, color: t.subtext }}>{field}</span>
            <div className="mt-0.5 h-5 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '20'}` }} />
          </div>
        </div>
      ))}
    </div>
  );

  const renderPrompt = () => (
    <div className="flex-1 flex flex-col">
      {prompts.length > 0 && (
        <div className="text-center mb-4 px-4">
          <p style={{ fontFamily: t.font, fontSize: 'clamp(0.85rem, 2vw, 1.1rem)', fontWeight: 600, fontStyle: 'italic', color: t.accent, lineHeight: 1.4 }}>
            &ldquo;{prompts[0]}&rdquo;
          </p>
        </div>
      )}
      <div className="space-y-3 flex-1 mt-2">
        {Array.from({ length: 14 }).map((_, i) => (
          <div key={i} className="h-px w-full" style={{ backgroundColor: t.fieldBorder || t.accent + '15' }} />
        ))}
      </div>
    </div>
  );

  const renderWeekly = () => (
    <div className="space-y-2 flex-1">
      {(page.fields || []).map((field, fi) => (
        <div key={fi}>
          <span style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', fontWeight: 600, color: meta.color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{field}</span>
          <div className="mt-0.5 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '20'}`, height: '2rem' }} />
        </div>
      ))}
    </div>
  );

  const renderGoals = () => (
    <div className="space-y-2 flex-1">
      {(page.fields || []).map((field, fi) => (
        <div key={fi} className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center" style={{ borderColor: t.accent + '40' }}>
            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: 'transparent' }} />
          </div>
          <span style={{ fontFamily: t.bodyFont, fontSize: '0.7rem', fontWeight: 500, color: t.text }}>{field}</span>
          <div className="flex-1 h-5 rounded" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
        </div>
      ))}
    </div>
  );

  const renderFreeWriting = () => (
    <div className="flex-1 flex flex-col">
      {page.content && (
        <p className="mb-3 text-center" style={{ fontFamily: t.font, fontSize: '0.7rem', fontStyle: 'italic', color: t.subtext }}>{page.content}</p>
      )}
      <div className="space-y-4 flex-1">
        {Array.from({ length: 16 }).map((_, i) => (
          <div key={i} className="h-px w-full" style={{ backgroundColor: t.fieldBorder || t.accent + '12' }} />
        ))}
      </div>
    </div>
  );

  const contentRenderer = {
    'journal-daily': renderDailyEntry,
    'journal-gratitude': renderGratitude,
    'journal-prompt': renderPrompt,
    'journal-weekly': renderWeekly,
    'journal-goals': renderGoals,
    'journal-free': renderFreeWriting,
  };

  const renderContent = contentRenderer[page.pageType];

  return (
    <div data-testid={`document-page-${pageIndex}`} className="w-full aspect-[210/297] flex flex-col relative overflow-hidden" style={{ backgroundColor: t.cardBg }}>
      <div className="h-1 w-full" style={{ backgroundColor: meta.color + '40' }} />
      <div className="px-7 pt-5 pb-3 flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: meta.color + '15' }}>
              <Icon className="w-2.5 h-2.5" style={{ color: meta.color }} strokeWidth={2} />
            </div>
            <span className="text-[8px] font-bold tracking-[0.15em] uppercase" style={{ fontFamily: t.bodyFont, color: meta.color }}>{meta.label}</span>
          </div>
          <h2 style={{ fontFamily: t.font, fontWeight: 700, fontSize: 'clamp(1rem, 2.5vw, 1.3rem)', color: t.text, lineHeight: 1.2 }}>{page.title}</h2>
        </div>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.subtext, opacity: 0.5 }}>{pageIndex + 1}/{totalPages}</span>
      </div>
      <div className="px-7 flex-1 overflow-hidden flex flex-col">
        {page.pageType === 'journal-intro' || page.pageType === 'journal-reflection' ? (
          <>
            {page.content && <p className="mb-3 leading-relaxed" style={{ fontFamily: t.bodyFont, fontSize: '0.7rem', color: t.subtext, lineHeight: 1.7 }}>{page.content}</p>}
            {page.fields && page.fields.length > 0 && renderWeekly()}
          </>
        ) : renderContent ? renderContent() : (
          <>
            {page.content && <p className="mb-2 leading-relaxed" style={{ fontFamily: t.bodyFont, fontSize: '0.7rem', color: t.subtext }}>{page.content}</p>}
            {page.fields && page.fields.length > 0 && renderWeekly()}
          </>
        )}
      </div>
      <div className="px-7 py-2 flex items-center justify-between" style={{ borderTop: `1px solid ${meta.color}10` }}>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4, letterSpacing: '0.1em' }}>LUMERA STUDIO</span>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4 }}>Page {pageIndex + 1}</span>
      </div>
    </div>
  );
};

export default JournalPage;
